import React, { createContext, useContext, useState, useCallback } from 'react';
import { toast } from 'sonner';
import api, { streamPost } from '../lib/api';

const ChatContext = createContext();

export function ChatProvider({ children }) {
  const [activeConversation, setActiveConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [panelOpen, setPanelOpen] = useState(false);
  const [layout, setLayout] = useState('panel'); // 'panel' | 'split' | 'fullscreen'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const startNewChat = useCallback(async (initialMessage, projectId = null, projectName = null) => {
    // If no message, just open an empty panel
    if (!initialMessage || !initialMessage.trim()) {
      setActiveConversation(null);
      setMessages([]);
      setPanelOpen(true);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);
    setPanelOpen(true);

    // Show optimistic user message + streaming assistant placeholder
    const tempUserMsg = {
      id: 'temp-user-' + Date.now(),
      role: 'user',
      content: initialMessage,
      created_at: new Date().toISOString(),
    };
    const streamingAssistantId = 'streaming-' + Date.now();
    const streamingMsg = {
      id: streamingAssistantId,
      role: 'assistant',
      content: '',
      streaming: true,
      toolStatus: null,
      created_at: new Date().toISOString(),
    };
    setMessages([tempUserMsg, streamingMsg]);
    setActiveConversation(null);

    try {
      await streamPost(
        '/conversations',
        { project_id: projectId, initial_message: initialMessage },
        (event) => {
          switch (event.type) {
            case 'conversation_created':
              setActiveConversation({
                id: event.conversation_id,
                title: event.title,
                project_id: projectId,
                project_name: projectName,
              });
              setMessages(prev =>
                prev.map(m =>
                  m.id === tempUserMsg.id
                    ? { ...m, id: event.user_message_id }
                    : m
                )
              );
              break;

            case 'text_delta':
              setMessages(prev =>
                prev.map(m =>
                  m.id === streamingAssistantId
                    ? { ...m, content: m.content + event.content }
                    : m
                )
              );
              break;

            case 'tool_status':
              setMessages(prev =>
                prev.map(m =>
                  m.id === streamingAssistantId
                    ? { ...m, toolStatus: { tool: event.tool, status: event.status } }
                    : m
                )
              );
              break;

            case 'done':
              setMessages(prev =>
                prev.map(m =>
                  m.id === streamingAssistantId
                    ? { ...m, content: event.answer || m.content, streaming: false, toolStatus: null }
                    : m
                )
              );
              break;

            case 'message_saved':
              setMessages(prev =>
                prev.map(m =>
                  m.id === streamingAssistantId
                    ? { ...m, id: event.assistant_message_id, streaming: false }
                    : m
                )
              );
              break;

            case 'error':
              setError(event.message);
              break;

            default:
              break;
          }
        }
      );
    } catch (err) {
      console.error('Failed to start new chat:', err);
      setMessages(prev => prev.filter(m => m.id !== streamingAssistantId));
      setError(err.message || 'Failed to start conversation');
      toast.error('Failed to start conversation');
      setPanelOpen(false);
    } finally {
      setLoading(false);
    }
  }, []);

  const openConversation = useCallback(async (conversationId) => {
    setLoading(true);
    setError(null);
    setPanelOpen(true);

    try {
      const response = await api.get(`/conversations/${conversationId}`);
      const data = response.data;
      setActiveConversation({
        id: data.id,
        title: data.title,
        project_id: data.project_id,
        project_name: data.project_name,
        is_saved: data.is_saved,
        message_count: data.message_count,
        created_at: data.created_at,
        updated_at: data.updated_at,
      });
      setMessages(data.messages || []);
    } catch (err) {
      console.error('Failed to open conversation:', err);
      setError(err.response?.data?.detail || 'Failed to load conversation');
      toast.error('Failed to load conversation');
    } finally {
      setLoading(false);
    }
  }, []);

  const sendMessage = useCallback(async (content, tools = null, projectIdOverride = null) => {
    if (!activeConversation?.id || !content.trim()) return;

    setError(null);

    // Optimistic user message
    const tempUserMsg = {
      id: 'temp-user-' + Date.now(),
      role: 'user',
      content,
      created_at: new Date().toISOString(),
    };
    // Placeholder for streaming assistant response
    const streamingAssistantId = 'streaming-' + Date.now();
    const streamingMsg = {
      id: streamingAssistantId,
      role: 'assistant',
      content: '',
      streaming: true,
      toolStatus: null,
      created_at: new Date().toISOString(),
    };

    setMessages(prev => [...prev, tempUserMsg, streamingMsg]);
    setLoading(true);

    try {
      await streamPost(
        `/conversations/${activeConversation.id}/messages`,
        { content, tools, project_id: projectIdOverride },
        (event) => {
          switch (event.type) {
            case 'text_delta':
              setMessages(prev =>
                prev.map(m =>
                  m.id === streamingAssistantId
                    ? { ...m, content: m.content + event.content }
                    : m
                )
              );
              break;

            case 'tool_status':
              setMessages(prev =>
                prev.map(m =>
                  m.id === streamingAssistantId
                    ? { ...m, toolStatus: { tool: event.tool, status: event.status } }
                    : m
                )
              );
              break;

            case 'done':
              setMessages(prev =>
                prev.map(m =>
                  m.id === streamingAssistantId
                    ? { ...m, content: event.answer || m.content, streaming: false, toolStatus: null }
                    : m
                )
              );
              break;

            case 'user_message':
              // Replace temp user message with real ID
              setMessages(prev =>
                prev.map(m =>
                  m.id === tempUserMsg.id
                    ? { ...m, id: event.message_id }
                    : m
                )
              );
              break;

            case 'message_saved':
              // Replace streaming ID with real assistant message ID
              setMessages(prev =>
                prev.map(m =>
                  m.id === streamingAssistantId
                    ? { ...m, id: event.assistant_message_id, streaming: false }
                    : m
                )
              );
              break;

            case 'error':
              setError(event.message);
              break;

            default:
              break;
          }
        }
      );
    } catch (err) {
      console.error('Failed to send message:', err);
      // Remove streaming message and mark user message as failed
      setMessages(prev =>
        prev
          .filter(m => m.id !== streamingAssistantId)
          .map(m => m.id === tempUserMsg.id ? { ...m, error: true } : m)
      );
      setError(err.message || 'Failed to send message');
      toast.error('Failed to send message');
    } finally {
      setLoading(false);
    }
  }, [activeConversation]);

  const retryMessage = useCallback(async (failedMessageId) => {
    const failedMsg = messages.find(m => m.id === failedMessageId && m.error);
    if (!failedMsg || !activeConversation?.id) return;

    // Clear error flag
    setMessages(prev => prev.map(m =>
      m.id === failedMessageId ? { ...m, error: false } : m
    ));
    setLoading(true);
    setError(null);

    try {
      const response = await api.post(`/conversations/${activeConversation.id}/messages`, {
        content: failedMsg.content,
      });
      const data = response.data;
      setMessages(prev => {
        const withoutFailed = prev.filter(m => m.id !== failedMessageId);
        return [...withoutFailed, data.user_message, data.assistant_message];
      });
      setActiveConversation(data.conversation);
    } catch (err) {
      console.error('Retry failed:', err);
      setMessages(prev => prev.map(m =>
        m.id === failedMessageId ? { ...m, error: true } : m
      ));
      toast.error('Retry failed');
    } finally {
      setLoading(false);
    }
  }, [activeConversation, messages]);

  const closePanel = useCallback(() => {
    setPanelOpen(false);
  }, []);

  const updateConversation = useCallback(async (updates) => {
    if (!activeConversation?.id) return;

    try {
      const response = await api.put(`/conversations/${activeConversation.id}`, updates);
      setActiveConversation(response.data);
    } catch (err) {
      console.error('Failed to update conversation:', err);
      toast.error('Failed to update conversation');
    }
  }, [activeConversation]);

  const deleteConversation = useCallback(async (conversationId) => {
    try {
      await api.delete(`/conversations/${conversationId}`);
      // If the deleted conversation is the active one, close the panel
      if (activeConversation?.id === conversationId) {
        setPanelOpen(false);
        setActiveConversation(null);
        setMessages([]);
      }
      return true;
    } catch (err) {
      console.error('Failed to delete conversation:', err);
      toast.error('Failed to delete conversation');
      return false;
    }
  }, [activeConversation]);

  return (
    <ChatContext.Provider value={{
      activeConversation,
      messages,
      panelOpen,
      layout,
      loading,
      error,
      startNewChat,
      openConversation,
      sendMessage,
      retryMessage,
      closePanel,
      setLayout,
      updateConversation,
      deleteConversation,
    }}>
      {children}
    </ChatContext.Provider>
  );
}

export function useChat() {
  const context = useContext(ChatContext);
  if (!context) throw new Error('useChat must be used within ChatProvider');
  return context;
}
